import { logError } from '@/lib/sentry.lib';

import dayjs from 'dayjs';
import { useReducer, useRef } from 'react';

import Button from '@ingka/button';
import FormField from '@ingka/form-field';
import InputField from '@ingka/input-field';
import { ListViewItem } from '@ingka/list-view';
import Modal, { ModalBody, ModalFooter, ModalHeader, Sheets } from '@ingka/modal';
import palletWithBoxes from '@ingka/ssr-icon/paths/pallet-with-boxes';
import { selectAssetDetails } from '@repo/api-adapters/selectors/customer-assets.selectors';
import { selectDeliveryContact } from '@repo/api-adapters/selectors/customer.selectors';
import { translate } from '@repo/phrase';
import { isOrderAssetDetails } from '@repo/types/order';
import { isQuotationAssetDetails } from '@repo/types/quotation';
import { getNotifier } from '@repo/utils';
import { invariant } from '@repo/utils/type.utils';
import { useMutation } from '@tanstack/react-query';

import { TELEMETRY_LOGS, logTelemetry } from '@/lib/telemetry.lib';

import { Feature } from '@/components/feature';
import { MODAL } from '@/components/modals/asset-modal.types';
import { QueryError } from '@/components/query-error';

import { getServiceEndpoint } from '@/api/helpers.api';
import { useAsset, useAssetParams } from '@/hooks/asset.hooks';
import { fetcher } from '@/utils/fetch.utils';
import { getAppConfig } from '@/utils/window.utils';

type NodeIds = Record<number, string>;

type ArrivalDates = Record<number, Date>;

const getItemUnitCode = (itemUnitCode: string) => {
    if (itemUnitCode === 'PIECES') {
        return 'Piece';
    }
    if (itemUnitCode === 'METERS') {
        return 'Meter';
    }
    return itemUnitCode;
};

export const ExtraNeeds = () => {
    const config = getAppConfig();
    const { assetId, assetType } = useAssetParams();
    const { data, isError, error } = useAsset(assetType, assetId);
    const [visible, toggleVisible] = useReducer((prev) => !prev, false);
    const nodeIds = useRef<NodeIds>({});
    const arrivalDates = useRef<ArrivalDates>({});
    const referenceId = useRef('');
    const assetData = selectAssetDetails(data);
    const deliveryContact = selectDeliveryContact(data);
    const isBusinessCustomer = Boolean(deliveryContact?.isBusinessCustomer);

    // Only draft asset type is supported
    invariant(
        !isOrderAssetDetails(assetData) && !isQuotationAssetDetails(assetData),
        'Extra needs supports only draft asset type'
    );

    const mutation = useMutation({
        mutationFn: () => {
            const payload = {
                message: {
                    draftId: assetId,
                    status: assetData?.status,
                    buCode: assetData?.buCode,
                    referenceId: referenceId.current,
                    countryCode: assetData?.countryCode,
                    createdBy: assetData?.createdBy,
                    customerInfo: {
                        ...assetData?.customerInfo,
                    },
                    // for each item in the cart, add the node id and arrival date, and change itemUnitCode if PIECES to Pieces
                    items: assetData?.cartData?.items?.map((item) => {
                        return {
                            itemId: item.itemId,
                            itemUnitCode: getItemUnitCode(item.retailItem.itemUnitCode),
                            quantity: item.quantity,
                            itemType: item.itemType,
                            unitPrice: item.unitPrice[0]?.currentPrice.priceInclTax ?? 0,
                            retailPrice: item.unitPrice[0]?.currentPrice.priceExclTax ?? 0,
                            nodeId: nodeIds.current[item.id]?.toUpperCase(),
                            expectedArrivalDate: arrivalDates.current[item.id]?.toISOString().slice(0, -5),
                        };
                    }),
                },
            };

            return fetcher(getServiceEndpoint(config.api.services.orderTakingBff, 'extraNeedsRequest'), {
                method: 'POST',
                body: JSON.stringify(payload),
                headers: { 'Content-Type': 'application/json' },
            });
        },
        onSuccess: () => {
            const notifier = getNotifier();
            notifier?.add({
                title: translate('extraneeds_success_title'),
                message: translate('extraneeds_success_message'),
            });
            toggleVisible();
        },
        onError: (error) => {
            const notifier = getNotifier();
            notifier?.add({
                title: translate('extraneeds_error_title'),
                message: translate('extraneeds_error_message'),
                type: 'error',
            });
            logError(error);
            toggleVisible();
        },
    });

    if (isError && !data) {
        return <QueryError textKey="drafts-extra-needs-queryError" error={error} />;
    }

    if (!assetId) {
        return null;
    }

    return (
        <Feature configPath={['extraNeedRequest.enable']} when={() => isBusinessCustomer}>
            <ListViewItem
                title={translate('extraneeds_title')}
                description={translate('extraneeds_action_label')}
                emphasised
                leadingIcon={palletWithBoxes}
                control="navigational"
                controlIcon="chevronRight"
                data-testid="toggle-extra-needs-btn"
                onClick={() => {
                    toggleVisible();
                    if (visible) {
                        logTelemetry('info', TELEMETRY_LOGS.ModalOpened, {
                            modal: {
                                name: MODAL.EXTRA_NEEDS,
                            },
                        });
                    }
                }}
            />
            <Modal handleCloseBtn={toggleVisible} visible={visible} data-testid="extra-needs-modal">
                <Sheets
                    aria-label={translate('extraneeds_title')}
                    header={<ModalHeader title={translate('extraneeds_title')} />}
                    footer={
                        <ModalFooter>
                            <Button
                                type="primary"
                                text={translate('extraneeds_send')}
                                disabled={mutation.isPending}
                                onClick={() => {
                                    mutation.mutate();
                                }}
                            />
                        </ModalFooter>
                    }
                >
                    <ModalBody>
                        <FormField>
                            <InputField
                                id="ot-reference-id"
                                type="text"
                                label={translate('extraneeds_reference_id')}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    referenceId.current = e.target.value;
                                }}
                            />
                        </FormField>
                        {assetData?.cartData?.items?.map((item) => {
                            if (item.itemId === '0') {
                                return null;
                            }
                            return (
                                <>
                                    <FormField key={item.id}>
                                        <InputField
                                            id={`ot-node-id-${item.id}`}
                                            type="text"
                                            label={translate('extraneeds_item_label', {
                                                name: item.name,
                                                art_id: item.itemId,
                                            })}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                nodeIds.current = {
                                                    ...nodeIds.current,
                                                    [item.id]: e.target.value,
                                                };
                                            }}
                                        />
                                    </FormField>
                                    <FormField>
                                        <InputField
                                            type="date"
                                            id={`ot-arrival-date-${item.id}`}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                arrivalDates.current = {
                                                    ...arrivalDates.current,
                                                    [item.id]: dayjs.utc(e.target.value).toDate(),
                                                };
                                            }}
                                            defaultValue={dayjs().format(config.dateFormat)}
                                        />
                                    </FormField>
                                    <hr />
                                </>
                            );
                        })}
                    </ModalBody>
                </Sheets>
            </Modal>
        </Feature>
    );
};
